import React, { createElement, FC, isValidElement, ReactNode, useEffect, useMemo } from 'react';

import { useTermsContext } from '~/context/GlossaryTermsProvider';
import StringWithTerms from '~/ui/components/TextWithTerms/StringWithTerms';
import useHash from '~/ui/components/TextWithTerms/useHash';

interface StringWithTermsProps {
  children?: ReactNode;
}

const WORDS_PER_TERM_LIMIT = 250;
const ALLOWED_TAGS = ['p', 'li'];
const calcWords = (text: string) => text.split(' ').length;

const TextWithTerms: FC<StringWithTermsProps> = ({ children }) => {
  const { glossaryTermsMap, refetch } = useTermsContext();
  const arrayOfChildren = useMemo(
    () => (Array.isArray(children) ? children : [children].filter(Boolean)),
    [children],
  );
  const textNodes = arrayOfChildren.filter((child) => typeof child === 'string');
  const hasTextNodes = textNodes.length > 0;
  const id = useHash(textNodes.join(''));

  const sanitizedTermsMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(glossaryTermsMap).map(([id, value]) => {
          const termsLimit = Math.max(1, calcWords(value.text) / WORDS_PER_TERM_LIMIT);
          return [id, value.terms.slice(0, termsLimit)];
        }),
      ) || {},
    [glossaryTermsMap],
  );

  // in case component was remounted or mounted later than others (e.g. when switching tabs) we should refetch terms
  useEffect(() => {
    if (!hasTextNodes) {
      return;
    }

    refetch();
  }, [refetch, hasTextNodes]);

  return (
    <>
      {arrayOfChildren.map((child, index) => {
        const { children, ...propsWithoutChildren } = child.props || {};

        if (
          isValidElement(child) &&
          typeof children === 'string' &&
          typeof child.type === 'string' &&
          ALLOWED_TAGS.includes(child.type)
        ) {
          return createElement(
            child.type,
            { ...propsWithoutChildren },
            <StringWithTerms key={index} parentId={id} termsById={sanitizedTermsMap}>
              {children}
            </StringWithTerms>,
          );
        }

        return (
          <StringWithTerms key={index} parentId={id} termsById={sanitizedTermsMap}>
            {child}
          </StringWithTerms>
        );
      })}
    </>
  );
};

export default TextWithTerms;
